import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import { useDispatch, useSelector } from "react-redux";
import {
  clearTaskActivityHistory,
  fetchTaskActivityHistory,
} from "../../redux/Slice/Tasks/taskActivityHistorySlice";

const formatWhen = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(value);
  }
};

const entityTypeLabel = (type) => (type === "task_template" ? "Template" : "Task");

const actionLabel = (action) => {
  const map = {
    "task.created": "Task created",
    "task.updated": "Task updated",
    "task.checklist_updated": "Checklist updated",
    "task.status_changed": "Status changed",
    "task.priority_changed": "Priority changed",
    "task.type_changed": "Type changed",
    "task.deleted": "Task deleted",
    "template.created": "Template created",
    "template.updated": "Template updated",
    "template.status_changed": "Template status",
    "template.priority_changed": "Template priority",
    "template.type_changed": "Template type",
    "template.deleted": "Template deleted",
  };
  return map[action] || action || "Activity";
};

/** Theme-aware circular history icon (drawer header + toolbar). */
export const historyIconCircleSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 36,
  height: 36,
  borderRadius: "50%",
  color: (theme) => theme.palette.primary.main,
  border: (theme) => `1.5px solid ${alpha(theme.palette.primary.main, 0.5)}`,
  backgroundColor: (theme) =>
    alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.1),
};

const historyTableSx = {
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? theme.palette.background.default : theme.palette.grey[100],
    backgroundImage: "none",
    color: (theme) => theme.palette.text.primary,
    fontWeight: 700,
    fontSize: "0.9rem",
    py: 1.35,
    lineHeight: 1.35,
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    color: (theme) => theme.palette.text.primary,
    fontSize: "0.875rem",
    py: 1.5,
    lineHeight: 1.5,
    verticalAlign: "top",
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
  },
  "& .MuiTableBody-root .MuiTypography-root": {
    color: (theme) => theme.palette.text.primary,
  },
  "& .MuiTableBody-root .MuiTypography-caption": {
    color: (theme) => theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
};

const TaskActivityHistoryDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { rows, pagination, retentionDays, loading, error } = useSelector(
    (state) => state.taskActivityHistory
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [entityType, setEntityType] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchTaskActivityHistory({
        page: page + 1,
        limit: rowsPerPage,
        search: search.trim(),
        entityType,
        action: actionFilter,
      })
    );
  }, [open, page, rowsPerPage, search, entityType, actionFilter, dispatch]);

  useEffect(() => {
    if (!open) {
      dispatch(clearTaskActivityHistory());
      setPage(0);
      setSearch("");
      setEntityType("");
      setActionFilter("");
    }
  }, [open, dispatch]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560, md: 680 },
          maxWidth: "100%",
          bgcolor: "background.paper",
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={historyIconCircleSx}>
            <HistoryIcon sx={{ fontSize: "1.25rem" }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.15rem" }}>
            Activity history
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close history">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, fontSize: "0.875rem", lineHeight: 1.45 }}
        >
          Showing the last {retentionDays} days for this account. Older entries are removed automatically.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
          <TextField
            size="small"
            placeholder="Search logs..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ flex: "1 1 200px", minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              label="Entity"
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="task">Tasks</MenuItem>
              <MenuItem value="task_template">Templates</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer sx={{ flex: 1, overflow: "auto" }}>
        <Table stickyHeader sx={historyTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Summary</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell colSpan={3}>
                    <Skeleton height={36} />
                  </TableCell>
                </TableRow>
              ))}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="error" sx={{ fontSize: "0.875rem" }}>
                    {String(error)}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="text.secondary" sx={{ py: 2, fontSize: "0.875rem" }}>
                    No activity recorded in the last {retentionDays} days.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !error &&
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                      {formatWhen(row.createdAt)}
                    </Typography>
                    <Chip
                      size="small"
                      label={entityTypeLabel(row.entityType)}
                      sx={{
                        mt: 0.75,
                        height: 24,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.primary.main, 0.12),
                        color: (theme) => theme.palette.primary.main,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
                      {row.actorName || "Member"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                      {actionLabel(row.action)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.summary || ""} placement="top-start">
                      <Typography sx={{ fontSize: "0.875rem", lineHeight: 1.55 }}>
                        {row.summary}
                      </Typography>
                    </Tooltip>
                    {row.entityName ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.75, fontSize: "0.8rem" }}
                      >
                        {row.entityName}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.total || 0}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: "divider",
          color: "text.primary",
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: "0.875rem",
            color: "text.primary",
          },
        }}
      />
    </Drawer>
  );
};

export default TaskActivityHistoryDrawer;
